import { EMPTY_AUDIENCE } from '@fundline/shared/schemas/campaigns';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge, type BadgeRole } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import {
  type CampaignStatus,
  type CampaignSummary,
  useCampaignsQuery,
  useCreateCampaign,
  usePauseAll,
} from './use-campaigns.ts';

export const CAMPAIGN_STATUS: Record<CampaignStatus, { label: string; role: BadgeRole }> = {
  draft: { label: 'Draft', role: 'neutral' },
  scheduled: { label: 'Scheduled', role: 'new' },
  running: { label: 'Sending', role: 'pending' },
  paused: { label: 'Paused', role: 'pending' },
  completed: { label: 'Done', role: 'funded' },
  cancelled: { label: 'Cancelled', role: 'declined' },
};

export const DEFAULT_TEMPLATE =
  'Hi {first_name|there}, this is {rep_first_name|your rep} at {company}. ';

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function line(c: CampaignSummary): string {
  const n = (v: number) => v.toLocaleString('en-US');
  switch (c.status) {
    case 'draft':
      return `Created ${when(c.createdAt)}`;
    case 'scheduled':
      return c.scheduledAt ? `Launches ${when(c.scheduledAt)}` : 'Scheduled';
    case 'running':
    case 'paused':
      return `${n(c.eligibleCount)} getting a text · ${n(c.skippedCount)} skipped`;
    case 'completed':
      return `Done ${c.completedAt ? when(c.completedAt) : ''} · ${n(c.eligibleCount)} texted`;
    case 'cancelled':
      return `Cancelled ${c.cancelledAt ? when(c.cancelledAt) : ''}`;
  }
}

/** Campaigns (M9, admins): the list, a new draft, and the emergency pause. */
export function CampaignsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const navigate = useNavigate();
  const campaigns = useCampaignsQuery(workspaceId);
  const create = useCreateCampaign(workspaceId);
  const pauseAll = usePauseAll(workspaceId);
  const [error, setError] = useState<string | null>(null);
  const live = (campaigns.data ?? []).filter(
    (c) => c.status === 'running' || c.status === 'scheduled',
  );

  async function startNew() {
    setError(null);
    try {
      const { campaignId } = await create.mutateAsync({
        name: `New campaign ${new Date().toLocaleDateString()}`,
        audience: EMPTY_AUDIENCE,
        template: DEFAULT_TEMPLATE,
        maxSegments: 1,
        frequencyDays: 7,
        aiEnabled: false,
        aiModel: 'haiku',
      });
      await navigate({ to: '/campaigns/$campaignId', params: { campaignId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create the campaign.");
    }
  }

  async function stopEverything() {
    if (
      !window.confirm(
        'Pause every sending campaign and move scheduled ones back to drafts? Texts already with the carrier still arrive.',
      )
    ) {
      return;
    }
    setError(null);
    try {
      await pauseAll.mutateAsync();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't pause the campaigns.");
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Campaigns"
        description="Text a group of merchants. Every text follows sending hours, opt-outs and caps."
        actions={
          <>
            {live.length > 0 ? (
              <Button
                variant="secondary"
                busy={pauseAll.isPending}
                onClick={() => void stopEverything()}
              >
                Pause all
              </Button>
            ) : null}
            <Button busy={create.isPending} onClick={() => void startNew()}>
              New campaign
            </Button>
          </>
        }
      />
      {error ? <Alert tone="error">{error}</Alert> : null}
      {pauseAll.data ? (
        <Alert tone="success">
          {pauseAll.data.paused === 0
            ? 'Nothing was sending.'
            : `Paused ${pauseAll.data.paused.toLocaleString('en-US')} ${pauseAll.data.paused === 1 ? 'campaign' : 'campaigns'}.`}
        </Alert>
      ) : null}
      {campaigns.isPending ? <Spinner label="Loading campaigns" /> : null}
      {campaigns.isError ? <Alert tone="error">{campaigns.error.message}</Alert> : null}
      {campaigns.data?.length === 0 ? (
        <EmptyState title="No campaigns yet">
          Start one to text a tag, a lead source or an import. You'll see who gets it before
          anything goes out.
        </EmptyState>
      ) : null}
      {campaigns.data && campaigns.data.length > 0 ? (
        <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
          {campaigns.data.map((c) => (
            <li key={c.id}>
              <Link
                to="/campaigns/$campaignId"
                params={{ campaignId: c.id }}
                className="flex flex-wrap items-center justify-between gap-2 p-3 hover:bg-surface focus-visible:outline-2 focus-visible:outline-focus-ring"
                data-testid="campaign-row"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-ink">{c.name}</span>
                  <span className="footnote text-ink-muted">{line(c)}</span>
                </span>
                <Badge role={CAMPAIGN_STATUS[c.status].role}>
                  {CAMPAIGN_STATUS[c.status].label}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
